/**
 * Mixin per il widget anti-bot Cloudflare Turnstile.
 *
 * Gestisce uno o piu' widget nello stesso componente: ogni widget e' identificato
 * dal nome del suo ref, e ha un token indipendente dagli altri.
 *
 * Uso con un solo widget (il caso normale):
 *   1. <div ref="turnstile"></div> nel form
 *   2. this.loadTurnstile() nel mounted
 *   3. if (this.turnstileMissing()) { ...errore...; return; } prima dell'invio
 *   4. inviare this.withTurnstileToken(payload)
 *   5. this.resetTurnstile() al termine di OGNI tentativo (il token e' usa e getta)
 *
 * Uso con piu' widget, passando i nomi dei ref:
 *   this.loadTurnstile(["turnstile", "turnstileNewsletter"]);
 *   this.turnstileMissing("turnstileNewsletter")
 *   this.withTurnstileToken(payload, "turnstileNewsletter")
 *   this.resetTurnstile("turnstileNewsletter")
 *
 * Se VUE_APP_TURNSTILE_SITEKEY non e' configurata i widget non vengono caricati
 * e i form continuano a funzionare come prima.
 */
var DEFAULT_REF = "turnstile";

export default {
  data() {
    return {
      turnstileSiteKey: process.env.VUE_APP_TURNSTILE_SITEKEY,
      // nome del ref -> { widgetId, token }
      turnstileWidgets: {},
      turnstileRefNames: [],
    };
  },
  methods: {
    loadTurnstile: function (refNames) {
      var names = refNames == null
        ? [DEFAULT_REF]
        : (typeof refNames === "string" ? [refNames] : refNames);

      this.turnstileRefNames = names;

      names.forEach((name) => {
        if (!this.turnstileWidgets[name]) {
          // $set perche' in Vue 2 le chiavi aggiunte a mano non sono reattive
          this.$set(this.turnstileWidgets, name, { widgetId: null, token: "" });
        }
      });

      if (!this.turnstileSiteKey) {
        console.warn(
          "VUE_APP_TURNSTILE_SITEKEY non configurata: widget anti-bot non caricato"
        );
        return;
      }

      if (window.turnstile) {
        this.renderTurnstile();
        return;
      }

      var existingScript = document.getElementById("cf-turnstile-script");

      if (existingScript) {
        // Un altro componente ha gia' avviato il caricamento dello script.
        existingScript.addEventListener("load", this.renderTurnstile);
        return;
      }

      var script = document.createElement("script");
      script.id = "cf-turnstile-script";
      script.src =
        "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.onload = this.renderTurnstile;
      document.head.appendChild(script);
    },
    renderTurnstile: function () {
      this.turnstileRefNames.forEach(this.renderTurnstileWidget);
    },
    renderTurnstileWidget: function (refName) {
      var container = this.$refs[refName];
      var state = this.turnstileWidgets[refName];

      if (!window.turnstile || !container || !state) {
        return;
      }

      if (state.widgetId != null) {
        // Gia' renderizzato: evito widget doppi se l'evento load arriva due volte.
        return;
      }

      state.widgetId = window.turnstile.render(container, {
        sitekey: this.turnstileSiteKey,
        callback: (token) => {
          state.token = token;
        },
        "expired-callback": () => {
          state.token = "";
        },
        "error-callback": () => {
          state.token = "";
        },
      });
    },
    resetTurnstile: function (refName) {
      var name = refName || DEFAULT_REF;
      var state = this.turnstileWidgets[name];

      if (!state) {
        return;
      }

      state.token = "";

      var container = this.$refs[name];

      // A invio riuscito il form sparisce dal DOM: non c'e' piu' nulla da resettare.
      if (
        !window.turnstile ||
        state.widgetId == null ||
        !container ||
        !document.body.contains(container)
      ) {
        return;
      }

      try {
        window.turnstile.reset(state.widgetId);
      } catch (resetError) {
        console.warn("Reset del widget Turnstile non riuscito", resetError);
      }
    },
    /** true se il widget e' attivo ma l'utente non ha ancora superato la verifica */
    turnstileMissing: function (refName) {
      var state = this.turnstileWidgets[refName || DEFAULT_REF];

      return Boolean(this.turnstileSiteKey) && (!state || state.token == "");
    },
    /** aggiunge al payload il token del widget indicato */
    withTurnstileToken: function (payload, refName) {
      var state = this.turnstileWidgets[refName || DEFAULT_REF];

      return Object.assign({}, payload, {
        turnstileToken: state ? state.token : "",
      });
    },
  },
};
